import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { selectSession } from '../model/selectors';
import { SessionType, isRunningLocal, builtEnvironment } from '@shared';

const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 1000;

function buildClientSignInFallbackUrl(formReferenceId: string): string {
  const redirectTo = `/onboarding-form/${formReferenceId}`;
  const params = new URLSearchParams({
    client_sign_in: 'true',
    redirect_to: redirectTo,
  });
  return `/?${params.toString()}`;
}

async function fetchWithRetry(url: string, retries: number): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url);
      if (response.ok) {
        return response;
      }
      if (attempt < retries && response.status >= 500) {
        await new Promise(r => setTimeout(r, RETRY_BASE_DELAY_MS * Math.pow(2, attempt)));
        continue;
      }
      return response;
    } catch (error) {
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, RETRY_BASE_DELAY_MS * Math.pow(2, attempt)));
        continue;
      }
      throw error;
    }
  }
  throw new Error('Exhausted retries');
}

export function OnboardingRedirect() {
  const { formReferenceId } = useParams<{ formReferenceId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const session = useSelector(selectSession);
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    if (!formReferenceId || hasRedirected) {
      return;
    }

    if (authLoading) {
      return;
    }

    if (isAuthenticated && session.sessionType === SessionType.unknown) {
      return;
    }

    if (isAuthenticated && (session.sessionType === SessionType.client || session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard)) {
      setHasRedirected(true);
      navigate(`/onboarding-form/${formReferenceId}`, { replace: true });
      return;
    }

    if (!isAuthenticated) {
      setHasRedirected(true);
      
      async function handleRedirect() {
        try {
          const env = isRunningLocal() ? 'dev' : builtEnvironment;
          const response = await fetchWithRetry(`/${env}/api/onboarding/${formReferenceId}`, MAX_RETRIES);
          
          if (!response.ok) {
            console.warn('Onboarding redirect API returned non-OK status, falling back to client sign-in flow');
            navigate(buildClientSignInFallbackUrl(formReferenceId!), { replace: true });
            return;
          }

          const data = await response.json();
          if (data.localStorageAllowed === true && data.surveyUrl) {
            window.location.href = data.surveyUrl;
          } else {
            window.location.href = data.redirectUrl;
          }
        } catch (error) {
          console.error('Error fetching onboarding redirect after retries:', error);
          navigate(buildClientSignInFallbackUrl(formReferenceId!), { replace: true });
        }
      }

      handleRedirect();
    }
  }, [formReferenceId, navigate, isAuthenticated, authLoading, session.sessionType, hasRedirected]);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
      </div>
    </div>
  );
}

